//@flow

import {useState, useCallback, useEffect} from 'react';
import {
  getStoreStockSettingsService,
  saveStoreStockSettingsService,
} from '../promises';
import {useToastActions} from '../../../toast-alert/utils';
import type {
  StoreStockSettings,
  StoreStockSettingsRequest,
} from '../../../../entities';

export function useStoreStockSetting() {
  const [settings, setSettings] = useState<StoreStockSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const {showToast} = useToastActions();

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getStoreStockSettingsService();
      setSettings(response);
    } catch (error) {
      console.error('Error loading store stock settings:', error);
      setError('Error al cargar la configuración de stock');
      showToast({
        type: 'danger',
        title: 'Error al cargar la configuración',
        message: 'Ocurrió un error al cargar la configuración de stock',
      });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const saveSettings = useCallback(
    async (storeStockSettings: StoreStockSettingsRequest) => {
      setSaving(true);
      setError(null);
      try {
        const result = await saveStoreStockSettingsService(storeStockSettings);
        setSettings(result);
        showToast({
          type: 'success',
          title: 'Configuración guardada',
          message: 'La configuración de stock se ha guardado correctamente',
        });
        return storeStockSettings;
      } catch (error) {
        console.error('Error saving store stock settings:', error);
        setError('Error al guardar la configuración de stock');
        showToast({
          type: 'danger',
          title: 'Error al guardar',
          message: 'Ocurrió un error al guardar la configuración de stock',
        });
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [showToast]
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      setError(null);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  return {
    settings,
    loading,
    saving,
    error,
    loadSettings,
    saveSettings,
  };
}
